import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Users, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { successPop } from "@/lib/celebrate";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import { PageExplain } from "@/components/PageExplain";
import { MemberCard, MemberGrid } from "@/components/MemberCard";

const PAGE = 60;
const STEPS = [
  "Unter „Alle“ siehst du alle Mitglieder der Community – mit der Suche findest du jemanden schnell per Name.",
  "Tippe auf ein Profil, um es anzusehen, auf die Pinnwand zu schreiben oder eine Freundschaftsanfrage zu senden.",
  "Unter „Anfragen“ nimmst du Freundschaftsanfragen an oder lehnst sie ab. Bei neuen Anfragen bekommst du eine Push-Nachricht.",
  "Unter „Freunde“ siehst du alle deine Freunde – und in der Rangliste kannst du dich nur mit ihnen vergleichen.",
];
const TABS = [["all", "Alle"], ["friends", "Freunde"], ["requests", "Anfragen"]];
const Spinner = () => <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;

function AllMembers() {
  const [q, setQ] = useState("");
  const [list, setList] = useState(null);
  const [more, setMore] = useState(false);
  const fetchPage = useCallback((skip) => api.get("/community/members", { params: { q, skip } }).then(({ data }) => { setMore(data.length === PAGE); return data; }), [q]);
  useEffect(() => { const t = setTimeout(() => fetchPage(0).then(setList), 250); return () => clearTimeout(t); }, [fetchPage]);
  const loadMore = () => fetchPage(list.length).then((d) => setList((l) => [...l, ...d]));
  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Benutzername suchen…" data-testid="community-search-input"
          className="w-full h-12 pl-11 pr-4 rounded-full border border-pink-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" />
      </div>
      {!list ? <Spinner /> : <MemberGrid list={list} empty="Niemand gefunden." />}
      {more && <button onClick={loadMore} data-testid="community-load-more" className="mx-auto block px-6 h-11 rounded-full bg-white border border-pink-200 text-sm font-semibold text-rose-600 hover:bg-rose-50">Mehr laden</button>}
    </div>
  );
}

function MyFriends() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  useEffect(() => { api.get(`/users/${user.user_id}/friends`).then(({ data }) => setList(data)); }, [user.user_id]);
  return !list ? <Spinner /> : <MemberGrid list={list} testId="community-friends" empty="Noch keine Freunde – schick unter „Alle“ deine erste Anfrage!" />;
}

function Requests({ onCount }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get("/friends/requests").then(({ data }) => { setData(data); onCount(data.incoming.length); }), [onCount]);
  useEffect(() => { load(); }, [load]);
  const act = async (m, kind) => {
    try { await api.post(`/friends/requests/${m.request_id}/${kind}`); if (kind === "accept") successPop(); toast.success(kind === "accept" ? `Du bist jetzt mit ${m.name} befreundet 💞` : "Anfrage abgelehnt"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const cancel = async (m) => { try { await api.delete(`/friends/${m.user_id}`); toast.success("Anfrage zurückgezogen"); load(); } catch (e) { toast.error(formatErr(e)); } };
  if (!data) return <Spinner />;
  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="font-bold">Erhaltene Anfragen</h3>
        <MemberGrid list={data.incoming} testId="requests-incoming" empty="Keine offenen Anfragen." render={(m, i) => (
          <MemberCard key={m.user_id} m={m} i={i}>
            <div className="grid grid-cols-2 gap-1.5 w-full">
              <button onClick={() => act(m, "accept")} data-testid={`request-accept-${m.user_id}`} className="h-9 rounded-xl pink-gradient text-white flex items-center justify-center"><Check className="w-4 h-4" /></button>
              <button onClick={() => act(m, "decline")} data-testid={`request-decline-${m.user_id}`} className="h-9 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
            </div>
          </MemberCard>
        )} />
      </section>
      <section className="space-y-3">
        <h3 className="font-bold">Gesendete Anfragen</h3>
        <MemberGrid list={data.outgoing} testId="requests-outgoing" empty="Du hast keine Anfragen offen." render={(m, i) => (
          <MemberCard key={m.user_id} m={m} i={i}>
            <button onClick={() => cancel(m)} data-testid={`request-cancel-${m.user_id}`} className="w-full h-9 rounded-xl bg-slate-100 hover:bg-slate-200 text-xs font-semibold">Zurückziehen</button>
          </MemberCard>
        )} />
      </section>
    </div>
  );
}

export default function Community() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.some(([k]) => k === params.get("tab")) ? params.get("tab") : "all";
  const [incoming, setIncoming] = useState(0);
  useEffect(() => { api.get("/friends/requests").then(({ data }) => setIncoming(data.incoming.length)).catch(() => {}); }, []);
  return (
    <div className="space-y-6">
      <div>
        <p className="text-xs tracking-wider uppercase font-semibold text-rose-600">Community</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3"><Users className="w-8 h-8 text-rose-500" /> Mitglieder</h1>
      </div>
      <PageExplain testId="community-explain" steps={STEPS} />
      <div className="flex gap-2 p-1 rounded-full bg-white border border-pink-100 w-fit">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setParams(k === "all" ? {} : { tab: k })} data-testid={`community-tab-${k}`}
            className={`relative px-4 h-9 rounded-full text-sm font-semibold transition-colors ${tab === k ? "bg-rose-500 text-white" : "text-slate-600 hover:bg-rose-50"}`}>
            {l}{k === "requests" && incoming > 0 && <span data-testid="community-requests-badge" className="ml-1.5 inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-amber-400 text-amber-950 text-[11px] font-bold">{incoming}</span>}
          </button>
        ))}
      </div>
      {tab === "all" && <AllMembers />}
      {tab === "friends" && <MyFriends />}
      {tab === "requests" && <Requests onCount={setIncoming} />}
    </div>
  );
}
